import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';

@Component({
  selector: 'simx-file-input',
  standalone: false,
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.scss'],
  host: {
    class: 'angular-component-wrapper',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FileInputComponent,
    },
  ],
})
export class FileInputComponent implements ControlValueAccessor {
  @Input('style') givenStyle: any = null;

  @ViewChild('fileInput') fileInput?: ElementRef;

  selectedFile$: Subject<File | null> = new Subject<File | null>();

  fileDragInProgress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  disabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  touched$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  onTouched: Function = () => {};

  onChange: Function = (value: File | null) => {};

  registerOnChange(onChange: Function): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: Function): void {
    this.onTouched = onTouched;
  }

  writeValue(value: File | null): void {
    this.selectedFile$.next(value);
  }

  setSelectedFile(file: File | null): void {
    this.selectedFile$.next(file);
    this.onChange(file);
  }

  setFileDragInProgress(state: boolean): void {
    this.fileDragInProgress$.next(state);
  }

  setDisabledState(state: boolean): void {
    this.disabled$.next(state);
  }

  handleFileInputChange(): void {
    const files: FileList = this.fileInput?.nativeElement.files;
    this.setSelectedFile(files.length ? files[0] : null);
  }

  async handleFileDragEnter(event: Event): Promise<void> {
    event.preventDefault();

    const currentlyDisabled = await firstValueFrom(this.disabled$);
    if (!currentlyDisabled) {
      this.setFileDragInProgress(true);
    }
  }

  async handleFileDragLeave(event: Event): Promise<void> {
    event.preventDefault();

    const currentlyDisabled = await firstValueFrom(this.disabled$);
    if (!currentlyDisabled) {
      this.setFileDragInProgress(false);
    }
  }

  handleFileDragOver(event: Event): void {
    event.preventDefault();
  }

  async handleFileDrop(event: DragEvent): Promise<void> {
    event.preventDefault();

    const currentlyDisabled = await firstValueFrom(this.disabled$);
    if (!currentlyDisabled) {
      const files = event.dataTransfer?.files;
      this.setSelectedFile(files && files.length ? files[0] : null);
      this.setFileDragInProgress(false);
    }
  }

  async handleSelectFileClick(): Promise<void> {
    const currentlyDisabled = await firstValueFrom(this.disabled$);
    if (!currentlyDisabled) {
      this.invokeFileSelector();
    }
  }

  async handleSelectOtherFileClick(): Promise<void> {
    const currentlyDisabled = await firstValueFrom(this.disabled$);
    if (!currentlyDisabled) {
      this.invokeFileSelector();
    }
  }

  async handleRemoveSelectedFileClick(): Promise<void> {
    const currentlyDisabled = await firstValueFrom(this.disabled$);
    if (!currentlyDisabled) {
      this.setSelectedFile(null);
    }
  }

  invokeFileSelector(): void {
    this.fileInput?.nativeElement.click();
  }
}
